import { StoreInfo } from '../types';

// Street part of an address. Usually everything before the first comma, but
// receipts often print "2100 Van Ness Ave. San Francisco" with no comma before
// the city, so stop after the street type (keeping a trailing direction, as in
// "685 Schillinger Rd S").
const STREET_END = /^(.*?\d.*?\b(?:Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard|Dr|Drive|Way|Ln|Lane|Hwy|Highway|Pkwy|Parkway|Ct|Court|Pl|Place|Cir|Circle|Ter|Terrace|Sq|Square|Trl|Trail)\b\.?(?:\s+(?:N|S|E|W|NE|NW|SE|SW)\b\.?)?)(?=\s|$)/i;
export const streetPart = (address: string): string => {
  const first = address.split(',')[0].trim();
  const match = first.match(STREET_END);
  return (match ? match[1] : first).trim().replace(/\.$/, '');
};

export const getFormattedStoreDisplayName = (store: StoreInfo | null | undefined): string => {
  if (!store || typeof store.name !== 'string' || !store.name.trim()) {
    return 'Unknown Store';
  }
  const cleanStoreName = store.name.split(',')[0].trim();
  if (typeof store.address !== 'string' || !store.address.trim()) {
    return cleanStoreName;
  }
  const streetAddress = streetPart(store.address);
  if (streetAddress && !cleanStoreName.toLowerCase().includes(streetAddress.toLowerCase())) {
    return `${cleanStoreName}, ${streetAddress}`;
  }
  return cleanStoreName;
};

/**
 * Returns store coordinates only when they are real, plausible lat/lng values.
 * Gemini sometimes echoes placeholder coordinates (0, 0) — "Null Island" —
 * which stacks every marker in the Atlantic and breaks map bounds.
 */
export const sanitizeStoreCoordinates = (
  coords: any,
): { lat: number; lng: number } | undefined => {
  if (!coords) return undefined;
  const lat = Number(coords.lat);
  const lng = Number(coords.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return undefined;
  if (Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01) return undefined;
  return { lat, lng };
};

/** Recorded street addresses for the store keys included in a comparison. */
export const battleStoreStreets = (itemData: Record<string, any> | null | undefined, storeNames: string[]): string[] => {
  const names = new Set(storeNames.map(name => name.toLowerCase().trim()));
  const streets = new Set<string>();
  for (const item of Object.values(itemData || {})) {
    for (const [name, history] of Object.entries(item?.stores || item || {})) {
      if (!names.has(name.toLowerCase().trim()) || !Array.isArray(history)) continue;
      for (const point of history) {
        if (typeof point?.address === 'string' && point.address.trim()) streets.add(streetPart(point.address));
      }
    }
  }
  return [...streets].filter(Boolean);
};
